import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { formatClinicDate, clinicTimeToUTC } from '../../utils/timezone';
import { useAvailabilityData } from '../../contexts/AvailabilityDataContext';
import { useAvailabilityUI } from '../../contexts/AvailabilityUIContext';
import { useAvailabilityCRUD } from '../../hooks/useAvailabilityCRUD';
import { CreateMemberAvailabilityRequest, RecurrenceType } from '../../types/availability';
import { memberBookingAPI } from '../../services/api';
import { generateTimeOptions, formatTimeAMPM } from '../../utils/formatters';
import { Modal, Button } from '../ui';
import SelfBookingFields, {
  SelfBookingValues, defaultSelfBookingValues, clampSelfBooking, selfBookingEnd, validateSelfBooking,
  SelfBookingPolicyNote,
} from './SelfBookingFields';
import toast from 'react-hot-toast';
import { apiErrorMessage } from '../../utils/apiError';

// Step 'details' is the availability itself. Ticking "also reserve the room
// for myself" turns the submit into "Next" and step 'booking' holds the
// booking window (an explicit start + duration inside the new availability —
// never the whole range by default), with group size / client notification
// behind "More options".
type ModalStep = 'details' | 'booking';

const REPEAT_OPTIONS: { value: RecurrenceType; label: string }[] = [
  { value: 'one_time', label: 'Once' },
  { value: 'repeat_2_weeks', label: '2 weeks' },
  { value: 'repeat_4_weeks', label: '4 weeks' },
  { value: 'recurring', label: 'Every week' },
  { value: 'recurring_biweekly', label: 'Every 2 weeks' },
];

const fieldClass =
  'w-full px-3 py-2 border rounded-lg bg-white text-clinic-dark focus:outline-none focus:ring-2 ' +
  'focus:ring-clinic-primary/30 focus:border-clinic-primary disabled:bg-clinic-surface-sunken disabled:text-clinic-muted';

const CreateAvailabilityModal: React.FC = () => {
  const { state: dataState } = useAvailabilityData();
  const { state: uiState } = useAvailabilityUI();
  const { createAvailability, closeAllModals, fetchWeeklyAvailability } = useAvailabilityCRUD();

  const isQuickCreate = !!uiState.modals.create.prefilledData;

  const [formData, setFormData] = useState<CreateMemberAvailabilityRequest>({
    date: uiState.interaction.selectedDate ? format(uiState.interaction.selectedDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
    start_time: '09:00',
    end_time: '17:00',
    recurrence_type: 'one_time',
    recurrence_end_date: null,
    notes: null,
  });
  const [showNote, setShowNote] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<ModalStep>('details');
  const [reserveForMe, setReserveForMe] = useState(false);
  const [booking, setBooking] = useState<SelfBookingValues>(defaultSelfBookingValues('09:00', '17:00'));
  const [clientEmailError, setClientEmailError] = useState<string | null>(null);

  // Pre-fill from a drag-create on the calendar.
  useEffect(() => {
    const prefilled = uiState.modals.create.prefilledData;
    if (prefilled) {
      setFormData(prev => ({
        ...prev,
        ...prefilled,
        start_time: prefilled.start_time?.substring(0, 5) || prev.start_time,
        end_time: prefilled.end_time?.substring(0, 5) || prev.end_time,
      }));
    }
  }, [uiState.modals.create.prefilledData]);

  const globalHours = dataState.globalHours[formData.date];
  const timeOptions = useMemo(() => {
    const start = globalHours?.start_time?.substring(0, 5) || '06:00';
    const end = globalHours?.end_time?.substring(0, 5) || '22:00';
    return generateTimeOptions(start, end);
  }, [globalHours]);

  const wantsSelfBooking = reserveForMe && formData.recurrence_type === 'one_time';

  const validateDetails = (): boolean => {
    const next: Record<string, string> = {};
    if (!formData.date) next.date = 'Date is required';
    if (formData.start_time >= formData.end_time) next.end_time = 'End time must be after start time';
    if (globalHours) {
      if (formData.start_time < globalHours.start_time) next.start_time = `Clinic opens at ${formatTimeAMPM(globalHours.start_time)}`;
      if (formData.end_time > globalHours.end_time) next.end_time = `Clinic closes at ${formatTimeAMPM(globalHours.end_time)}`;
    }
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selected = new Date(formData.date + 'T00:00:00');
    if (selected < today) {
      next.date = 'Cannot create availability in the past';
    } else if (selected.getTime() === today.getTime()) {
      const [h, m] = formData.start_time.split(':').map(Number);
      if (h * 60 + m < now.getHours() * 60 + now.getMinutes()) next.start_time = 'Start time has already passed';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const goToBookingStep = () => {
    // Seed the booking window from the availability just entered.
    setBooking(defaultSelfBookingValues(formData.start_time, formData.end_time));
    setClientEmailError(null);
    setStep('booking');
  };

  const handleSubmit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (step === 'details') {
      if (!validateDetails()) return;
      if (wantsSelfBooking) { goToBookingStep(); return; }
    } else {
      const err = validateSelfBooking(booking);
      if (err) { setClientEmailError(err); return; }
    }

    setLoading(true);
    try {
      await createAvailability(formData);
      if (wantsSelfBooking) {
        try {
          const [h, m] = booking.start.split(':').map(Number);
          await memberBookingAPI.createSelfBooking({
            start_time: clinicTimeToUTC(formData.date, h, m),
            duration_minutes: booking.duration,
            num_attendees: Math.max(0, booking.groupSize - 1),
            client_name: booking.notifyClient ? booking.clientName.trim() || undefined : undefined,
            client_email: booking.notifyClient ? booking.clientEmail.trim() || undefined : undefined,
          });
          await fetchWeeklyAvailability();
          toast.success('Availability created and room reserved');
        } catch (selfBookError: any) {
          toast.error(apiErrorMessage(selfBookError, 'Availability created, but the room reservation failed'));
        }
      }
    } catch {
      setErrors({ submit: 'Failed to create availability. Please try again.' });
    } finally {
      setLoading(false);
      setStep('details');
    }
  };

  const setField = (name: keyof CreateMemberAvailabilityRequest, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const title = step === 'booking' ? 'Reserve the room' : isQuickCreate ? 'Quick add' : 'Add availability';
  const description = step === 'booking' || isQuickCreate
    ? `${formatClinicDate(formData.date, 'EEE, MMM d')} · ${formatTimeAMPM(formData.start_time)} – ${formatTimeAMPM(formData.end_time)}`
    : undefined;

  const footer = step === 'details' ? (
    <>
      <Button variant="ghost" onClick={closeAllModals} disabled={loading}>Cancel</Button>
      <Button type="submit" form="create-availability-form" isLoading={loading} disabled={loading} data-guide="create-submit">
        {wantsSelfBooking ? 'Next: reserve the room' : 'Add availability'}
      </Button>
    </>
  ) : (
    <>
      <Button variant="ghost" onClick={() => setStep('details')} disabled={loading}>Go back</Button>
      <Button onClick={() => handleSubmit()} isLoading={loading} disabled={loading} data-guide="create-selfbook-submit">
        Add &amp; reserve {formatTimeAMPM(booking.start)} – {formatTimeAMPM(selfBookingEnd(booking))}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen
      onClose={() => { if (!loading) closeAllModals(); }}
      title={title}
      description={description}
      size={isQuickCreate ? 'sm' : 'md'}
      footer={footer}
    >
      {step === 'details' ? (
        <form id="create-availability-form" onSubmit={handleSubmit} className="space-y-4">
          {!isQuickCreate && (
            <div data-guide="create-date">
              <label htmlFor="create-date" className="block text-sm font-medium text-clinic-dark mb-1">Date</label>
              <input
                id="create-date"
                type="date"
                value={formData.date}
                min={format(new Date(), 'yyyy-MM-dd')}
                onChange={(e) => setField('date', e.target.value)}
                className={`${fieldClass} ${errors.date ? 'border-clinic-accent' : 'border-clinic-primary-light'}`}
                disabled={loading}
              />
              {errors.date && <p className="mt-1 text-sm text-clinic-accent">{errors.date}</p>}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3" data-guide="create-times">
            <div>
              <label htmlFor="create-start" className="block text-sm font-medium text-clinic-dark mb-1">Start</label>
              <select
                id="create-start"
                value={formData.start_time}
                onChange={(e) => setField('start_time', e.target.value)}
                className={`${fieldClass} ${errors.start_time ? 'border-clinic-accent' : 'border-clinic-primary-light'}`}
                disabled={loading}
              >
                {timeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {errors.start_time && <p className="mt-1 text-sm text-clinic-accent">{errors.start_time}</p>}
            </div>
            <div>
              <label htmlFor="create-end" className="block text-sm font-medium text-clinic-dark mb-1">End</label>
              <select
                id="create-end"
                value={formData.end_time}
                onChange={(e) => setField('end_time', e.target.value)}
                className={`${fieldClass} ${errors.end_time ? 'border-clinic-accent' : 'border-clinic-primary-light'}`}
                disabled={loading}
              >
                {timeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {errors.end_time && <p className="mt-1 text-sm text-clinic-accent">{errors.end_time}</p>}
            </div>
          </div>
          <p className="text-xs text-clinic-muted -mt-2">
            {globalHours
              ? `Clinic hours ${formatTimeAMPM(globalHours.start_time)} – ${formatTimeAMPM(globalHours.end_time)}.`
              : 'No clinic hours are set for this day.'}
          </p>

          <div data-guide="create-repeat">
            <span className="block text-sm font-medium text-clinic-dark mb-1.5">Repeat</span>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Repeat">
              {REPEAT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={formData.recurrence_type === opt.value}
                  onClick={() => setField('recurrence_type', opt.value)}
                  disabled={loading}
                  className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                    formData.recurrence_type === opt.value
                      ? 'bg-clinic-primary text-white border-clinic-primary'
                      : 'bg-white text-clinic-dark border-clinic-primary-lighter hover:bg-clinic-cream/60'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {formData.recurrence_type === 'recurring' && (
              <p className="mt-1.5 text-xs text-clinic-muted">Repeats every week for the next 6 months.</p>
            )}
            {formData.recurrence_type === 'recurring_biweekly' && (
              <p className="mt-1.5 text-xs text-clinic-muted">Repeats every other week for the next 6 months.</p>
            )}
          </div>

          {formData.recurrence_type === 'one_time' && (
            <label className="flex items-start gap-3 cursor-pointer" data-guide="create-selfbook-toggle">
              <input
                type="checkbox"
                checked={reserveForMe}
                onChange={(e) => setReserveForMe(e.target.checked)}
                disabled={loading}
                className="mt-0.5 h-4 w-4 text-clinic-primary rounded border-clinic-primary-light focus:ring-clinic-primary/30"
              />
              <span className="text-sm text-clinic-dark">
                Also reserve the room for myself
                <span className="block text-xs text-clinic-muted">You'll pick the exact time on the next step.</span>
              </span>
            </label>
          )}

          {!isQuickCreate && (
            showNote ? (
              <div>
                <label htmlFor="create-note" className="block text-sm font-medium text-clinic-dark mb-1">Note</label>
                <textarea
                  id="create-note"
                  value={formData.notes || ''}
                  onChange={(e) => setField('notes', e.target.value)}
                  rows={2}
                  className={`${fieldClass} border-clinic-primary-light`}
                  placeholder="Anything to remember about this availability"
                  disabled={loading}
                />
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowNote(true)}
                className="text-sm text-clinic-primary hover:text-clinic-primary-dark"
                disabled={loading}
              >
                + Add a note
              </button>
            )
          )}

          {errors.submit && <p className="text-sm text-clinic-accent">{errors.submit}</p>}
        </form>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-clinic-muted">
            Choose the exact time you need. Only that window is booked and billed.
          </p>
          <SelfBookingFields
            windowStart={formData.start_time}
            windowEnd={formData.end_time}
            value={booking}
            onChange={(v) => { setBooking(clampSelfBooking(v, formData.start_time, formData.end_time)); setClientEmailError(null); }}
            disabled={loading}
            clientEmailError={clientEmailError}
          />
          <SelfBookingPolicyNote durationMinutes={booking.duration} />
        </div>
      )}
    </Modal>
  );
};

export default CreateAvailabilityModal;
